import type { DropdownData, FilteredValue, Value } from '../interfaces/input.interface';

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;
const DEFAULT_PLACEHOLDER_LABEL = '...';
const DEFAULT_LOADING_LABEL = 'Cargando...';
const SIN_VARIETY_LABEL = 'SIN VARIEDAD';
const DEFAULT_DROPDOWN_IDENTITY_KEYS = ['id', 'codigo', 'cod_producto', 'codigo_producto'] as const;

export interface ReactiveRequestHandler<TResponse = unknown> {
    unsuscribe(): unknown;
    response(callback: (value: TResponse) => void): unknown;
    safePerform(params?: unknown): unknown;
    fetchStatus?(callback: (status: 'syncing' | 'finish' | 'error') => void): unknown;
    isLoading?: boolean | undefined;
}

export type BuildTask = (resolve: (ok: boolean) => void) => void;

export type DropdownListItem<T> = T | [string, T | null];

export type DropdownLabelSelector<T = unknown> = string | readonly string[] | ((item: T) => unknown) | { or: readonly string[] };

type DropdownDataLike = DropdownData<unknown>;
type AnyReactiveRequestHandler = ReactiveRequestHandler<any>;

export type RequestResult<T> = T | readonly T[];


const activeBaseRequests = new WeakMap<ReactiveRequestHandler<unknown>, Map<string, Promise<unknown>>>();

function getRequestCacheKey(params: unknown): string {
    if (params === undefined) {
        return '__undefined__';
    }

    try {
        return JSON.stringify(params, Object.keys(params as Record<string, unknown>).sort());
    } catch {
        return String(params);
    }
}

function getActiveRequestMap(request: ReactiveRequestHandler<unknown>): Map<string, Promise<unknown>> {
    const current = activeBaseRequests.get(request);

    if (current) {
        return current;
    }

    const next = new Map<string, Promise<unknown>>();

    activeBaseRequests.set(request, next);

    return next;
}

function isDropdownFieldList<T>(field: DropdownLabelSelector<T>): field is readonly string[] {
    return Array.isArray(field);
}

function isDropdownOrSelector<T>(field: DropdownLabelSelector<T>): field is { or: readonly string[] } {
    return typeof field === 'object' && field !== null && 'or' in field;
}

function toDropdownOption<T>(item: DropdownListItem<T>): FilteredValue<T> {
    if (Array.isArray(item)) {
        return {
            label: item[0],
            value: item[1],
            raw: item[1],
            valueAliases: getDropdownAliases(item[1], item[0]),
        };
    }

    return {
        label: String(item),
        value: item,
        raw: item,
        valueAliases: getDropdownAliases(item, String(item)),
    };
}

function getDropdownAliases<T>(item: T | null, label: string): readonly unknown[] {
    if (item === null || item === undefined || typeof item !== 'object') {
        return [label].filter((value) => String(value ?? '').trim() !== '');
    }

    const record = item as Record<string, unknown>;
    const aliases = [
        record['id'],
        record['codigo'],
        record['cod_producto'],
        record['codigo_producto'],
        record['nombre'],
        record['razon_social'],
        record['descripcion'],
        label,
    ];

    return aliases.filter((value) => value !== null && value !== undefined && String(value).trim() !== '');
}

function readDropdownField<T>(item: T, field: string | undefined): unknown {
    if (field === undefined) {
        return undefined;
    }

    return (item as Record<string, unknown>)[field];
}

function getDropdownLabel<T>(item: T, field: DropdownLabelSelector<T>): string {
    if (typeof field === 'function') {
        return String(field(item) ?? '');
    }

    if (isDropdownFieldList(field)) {
        return field.map((fieldName) => readDropdownField(item, fieldName)).join(' ');
    }

    if (isDropdownOrSelector(field)) {
        const [firstField, secondField] = field.or;

        return String(readDropdownField(item, firstField) || readDropdownField(item, secondField) || '');
    }

    return String(readDropdownField(item, field) ?? '');
}

function getDropdownDistinctKey<T>(item: T, label: string): string {
    if (item === null || item === undefined || typeof item !== 'object') {
        return `label:${normalizeDropdownDistinctToken(label)}`;
    }

    const record = item as Record<string, unknown>;

    for (const identityKey of DEFAULT_DROPDOWN_IDENTITY_KEYS) {
        const identityValue = normalizeDropdownDistinctToken(record[identityKey]);

        if (identityValue !== '') {
            return `identity:${identityKey}:${identityValue}`;
        }
    }

    return `label:${normalizeDropdownDistinctToken(label)}`;
}

function normalizeDropdownDistinctToken(value: unknown): string {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
}

function sortDropdownOption<T>(first: FilteredValue<T>, second: FilteredValue<T>): number {
    return first.label.localeCompare(second.label, undefined, { sensitivity: 'base' });
}

function toItems<T>(value: RequestResult<T>): readonly T[] {
    return Array.isArray(value) ? value as readonly T[] : [value as T];
}

function assignDropdownData(target: DropdownDataLike, data: readonly FilteredValue<unknown>[], preserveExistingValues: boolean): void {
    if (!preserveExistingValues) {
        target.filtered = [...data];
        target.values = [...data];

        return;
    }

    const existingValues = target.values ?? [];
    const getId = (entry: Value<unknown> | FilteredValue<unknown>): unknown => {
        const value = entry.value as Record<string, unknown> | null | undefined;

        return value?.['id'];
    };
    const newIds = new Set(data.map(getId));
    const remainingItems = existingValues.filter((item) => !newIds.has(getId(item)));
    const combinedValues = [...data, ...remainingItems];

    target.filtered = [...combinedValues];
    target.values = [...combinedValues];
}

function assignDropdownTargets(
    target: DropdownDataLike | readonly DropdownDataLike[],
    data: readonly FilteredValue<unknown>[],
    preserveExistingValues: boolean,
): void {
    const targets = Array.isArray(target) ? target : [target];

    targets.filter(Boolean).forEach((entry) => {
        assignDropdownData(entry, data, preserveExistingValues);
    });
}

export function baseSentence<T>(
    data: readonly T[],
    field: DropdownLabelSelector<T>,
    noPlaceholder?: boolean,
): FilteredValue<T>[] {
    const distinctByOption = new Set<string>();
    const placeholder: FilteredValue<T>[] = noPlaceholder ? [] : [{ label: DEFAULT_PLACEHOLDER_LABEL, value: null }];
    const source = Array.isArray(data) ? data : [];
    const options = source
        .filter((item): item is T => item !== null && typeof item === 'object')
        .filter((item) => {
            const label = getDropdownLabel(item, field);
            const distinctKey = getDropdownDistinctKey(item, label);

            if (distinctByOption.has(distinctKey)) {
                // Exception for 'SIN VARIEDAD' to avoid filtering it out when repeated as a domain sentinel.
                return label === SIN_VARIETY_LABEL;
            }

            distinctByOption.add(distinctKey);

            return true;
        })
        .map((item) => {
            const label = getDropdownLabel(item, field);

            return {
                label,
                value: item,
                raw: item,
                valueAliases: getDropdownAliases(item, label),
            };
        })
        .sort(sortDropdownOption);

    return [...placeholder, ...options];
}

export function filterSentence<T extends Record<string, unknown>>(data: readonly T[], field: keyof T): FilteredValue<T>[] {
    const placeholder: FilteredValue<T>[] = [{ label: DEFAULT_PLACEHOLDER_LABEL, value: null }];
    const options: FilteredValue<T>[] = (Array.isArray(data) ? data : [])
        .filter((item): item is T => item !== null && typeof item === 'object')
        .sort((first, second) => String(first[field] ?? '').localeCompare(String(second[field] ?? ''), undefined, {
            sensitivity: 'base',
        }))
        .map((item) => ({
            label: String(item[field] ?? ''),
            value: item,
            raw: item,
            valueAliases: getDropdownAliases(item, String(item[field] ?? '')),
        }));

    return [...placeholder, ...options];
}

export function baseRequest<TResponse = unknown>(
    request: ReactiveRequestHandler<TResponse>,
    params?: unknown,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<TResponse> {
    const cacheRequest = request as ReactiveRequestHandler<unknown>;
    const requestMap = getActiveRequestMap(cacheRequest);
    const requestKey = getRequestCacheKey(params);
    const activeRequest = requestMap.get(requestKey);

    if (activeRequest) {
        return activeRequest as Promise<TResponse>;
    }

    const requestPromise = new Promise<TResponse>((resolve, reject): void => {
        const wasLoading = request.isLoading === true;

        if (!wasLoading) {
            request.unsuscribe();
        }

        let isSettled = false;
        let timeout: ReturnType<typeof globalThis.setTimeout>;
        const settle = (callback: () => void): void => {
            if (isSettled) {
                return;
            }

            isSettled = true;
            globalThis.clearTimeout(timeout);

            if (!wasLoading) {
                request.unsuscribe();
            }

            callback();
        };
        timeout = globalThis.setTimeout((): void => {
            settle(() => reject(new Error('Request timeout')));
        }, timeoutMs);

        request.fetchStatus?.((status): void => {
            if (status === 'error') {
                settle(() => reject(new Error('Request failed')));
            }
        });

        request.response((data: TResponse): void => {
            settle(() => resolve(data));
        });

        if (isSettled) {
            return;
        }

        try {
            request.safePerform(params);
        } catch (error: unknown) {
            settle(() => reject(error instanceof Error ? error : new Error(String(error))));
        }
    }).finally(() => {
        requestMap.delete(requestKey);
    });

    requestMap.set(requestKey, requestPromise as Promise<unknown>);

    return requestPromise;
}

export function list<T = unknown>(...values: DropdownListItem<T>[]): DropdownData<T> {
    const filtered: FilteredValue<T>[] = values.length > 0
        ? values.map(toDropdownOption)
        : [{ label: DEFAULT_PLACEHOLDER_LABEL, value: null }];

    return {
        filtered,
        values: [],
    };
}

export function filterList<T = unknown>(...values: DropdownListItem<T>[]): DropdownData<T> {
    const listValues: Value<T>[] = values.length > 0
        ? values.map(toDropdownOption)
        : [{ label: DEFAULT_LOADING_LABEL, value: null }];

    return {
        filtered: [],
        values: listValues,
    };
}

export function build(
    request: AnyReactiveRequestHandler | readonly AnyReactiveRequestHandler[],
    object: DropdownDataLike | readonly DropdownDataLike[],
    label: DropdownLabelSelector<any>,
    _filtrable?: boolean,
    noPlaceholder?: boolean,
): BuildTask {
    return (resolve: (resolved: boolean) => void): void => {
        const requests: readonly AnyReactiveRequestHandler[] = Array.isArray(request) ? request : [request];

        Promise.all(requests.map((entry) => baseRequest<unknown>(entry)))
            .then((results) => {
                // Combine all request results into a single dropdown source without coupling request and label types.
                const combined = results.flatMap((result) => toItems(result as RequestResult<unknown>));
                const data = baseSentence(combined, label, noPlaceholder);

                assignDropdownTargets(object, data, true);
                resolve(true);
            })
            .catch((error: unknown) => {
                console.error('Error loading dropdown data:', error);
                resolve(false);
            });
    };
}

export function buildWithParams(
    request: AnyReactiveRequestHandler,
    params: Record<string, unknown> | null | undefined,
    object: DropdownDataLike | readonly DropdownDataLike[],
    label: DropdownLabelSelector<any>,
    _filtrable?: boolean,
    noPlaceholder?: boolean,
): BuildTask {
    return (resolve: (resolved: boolean) => void): void => {
        baseRequest<unknown>(request, params ?? {})
            .then((data) => baseSentence(toItems(data as RequestResult<unknown>), label, noPlaceholder))
            .then((data) => {
                assignDropdownTargets(object, data, false);
                resolve(true);
            })
            .catch((error: unknown) => {
                console.error('Error loading dropdown params:', error);
                resolve(false);
            });
    };
}

export function stack(queue: any[], callback?: Function): Promise<any> {
    return new Promise(queue[0] || ((resolve) => resolve(null)))
        .then((next) => {
            if (callback) {
                callback();
            }

            if (next === true) {
                (queue || []).shift();

                if ((queue || []).length > 0) {
                    return stack(queue, callback);
                }

                return new Promise((resolve) => resolve(next)).then(() => next);
            }

            if (next === false) {
                return new Promise((resolve) => resolve(next)).then(() => next);
            }

            return new Promise((resolve) => resolve(next)).then(() => true);
        });
}

export function pool(queue: any[], callback?: Function): Promise<any> {
    return new Promise((resolve) => {
        const expected = (queue || []).length;

        if (expected === 0) {
            resolve(0);

            return;
        }

        let processed = 0;
        const checkHandlers = (): void => {
            if (callback) {
                callback();
            }

            processed += 1;

            if (expected === processed) {
                resolve(processed);
            }
        };

        (queue || []).forEach((requestTask) => {
            new Promise(requestTask)
                .then(checkHandlers)
                .catch((error: unknown): void => {
                    console.error('Error processing request pool item:', error);
                    checkHandlers();
                });
        });
    });
}

export function promiseMeanful(name: string, action: string): Promise<unknown> {
    console.log(name);
    console.log(action);

    return new Promise((resolve): void => {
        resolve(undefined);
    });
}

export function promise(): Promise<unknown> {
    return new Promise((resolve): void => {
        resolve(undefined);
    });
}
